import { QBankStructureLevelCategory, type Tag } from '@exam-management/graphql';
import { type QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import { groupBy, randomUUID } from '@shared/utils';

import { type QBankStructureDashboardData, type LearningObjectiveStatement } from '../../types';
import { findParentIds } from '../../utils';
import { getCalculateAndNonCalculateTotalFromLOSRow } from '../../utils/';
import {
  addLosToQBankStructureTree,
  generateLevelItemRows,
  generateLosRows,
} from '../../utils/dashboards/row-generator';
import { type SingleQuestionPropertiesData } from '../qbank-structure-single-question-dashboard-table';

function getTotalQuestionByLevelItemId(
  questionPropertiesData: SingleQuestionPropertiesData['questionProperties'],
  difficultyCategoryId: string,
  calculationCategoryId: string,
) {
  if (!questionPropertiesData) return null;

  const result = questionPropertiesData.find((tagGroup) => {
    const hasDifficultyTag = tagGroup.tags.some((tag) => tag.id === difficultyCategoryId);

    const hasCalculationTag = tagGroup.tags.some((tag) => tag.id === calculationCategoryId);
    return hasDifficultyTag && hasCalculationTag;
  });

  return result?.totalQuestions ?? null;
}

function generateSingleQuestionRows(
  questionPropertiesData: SingleQuestionPropertiesData[],
  difficultyMap: {
    hard: string;
    medium: string;
    easy: string;
  },
  calculationMap: {
    calc: string;
    nonCalc: string;
  },
): QBankStructureDashboardData[] {
  return questionPropertiesData.map((property, index) => ({
    parentId: property.locationId,
    // REMARK: this index is not displayed in the UI, used to satisfy the requirement of order field in QBankStructureDashboardData type
    order: index,
    id: randomUUID(),
    levelName: 'los',
    level: 'los' as const,
    parentIds: [],
    itemSetQuestions: [],
    calculate: {
      hardQuestions: getTotalQuestionByLevelItemId(
        property.questionProperties,
        difficultyMap.hard,
        calculationMap.calc,
      ),
      mediumQuestions: getTotalQuestionByLevelItemId(
        property.questionProperties,
        difficultyMap.medium,
        calculationMap.calc,
      ),
      easyQuestions: getTotalQuestionByLevelItemId(
        property.questionProperties,
        difficultyMap.easy,
        calculationMap.calc,
      ),
    },
    nonCalculate: {
      hardQuestions: getTotalQuestionByLevelItemId(
        property.questionProperties,
        difficultyMap.hard,
        calculationMap.nonCalc,
      ),
      mediumQuestions: getTotalQuestionByLevelItemId(
        property.questionProperties,
        difficultyMap.medium,
        calculationMap.nonCalc,
      ),
      easyQuestions: getTotalQuestionByLevelItemId(
        property.questionProperties,
        difficultyMap.easy,
        calculationMap.nonCalc,
      ),
    },
    total: property.questionProperties.reduce((sum, qp) => sum + qp.totalQuestions, 0),
  }));
}

const DISPLAY_QBANK_STRUCTURE_LEVEL_ITEM_CATEGORIES = [
  QBankStructureLevelCategory.Course,
  QBankStructureLevelCategory.Part,
  QBankStructureLevelCategory.Subject,
  QBankStructureLevelCategory.Group,
  QBankStructureLevelCategory.Chapter,
];

export function formatDataToQBankStructureLosSingleQuestionDashboardTable(input: {
  qBankStructureLevelItems: QBankStructureLevelItemWithLevel[];
  los: LearningObjectiveStatement[];
  questionPropertiesData: SingleQuestionPropertiesData[];
  difficultyTags: Tag[];
  calculationTags: Tag[];
}) {
  const { qBankStructureLevelItems, questionPropertiesData, los, difficultyTags, calculationTags } =
    input;

  const filteredQBankStructureLevelItems = qBankStructureLevelItems.filter((levelItem) =>
    DISPLAY_QBANK_STRUCTURE_LEVEL_ITEM_CATEGORIES.includes(levelItem.qBankStructureLevel.category),
  );

  const qbsRows = generateLevelItemRows(filteredQBankStructureLevelItems);

  const losRows = generateLosRows(los).map((losRow) => {
    const parentIds = findParentIds(qBankStructureLevelItems, losRow.parentId);
    return {
      ...losRow,
      parentIds,
    };
  });

  const losGroupByLevelItemId = groupBy(losRows, 'parentId');

  const qbsTreeWithLos = addLosToQBankStructureTree(qbsRows, losGroupByLevelItemId);

  const difficultyMap = {
    hard: difficultyTags.find((t) => t.name === 'ยาก')?.id ?? '',
    medium: difficultyTags.find((t) => t.name === 'กลาง')?.id ?? '',
    easy: difficultyTags.find((t) => t.name === 'ง่าย')?.id ?? '',
  };

  const calculationMap = {
    calc: calculationTags.find((t) => t.name === 'คำนวณ')?.id ?? '',
    nonCalc: calculationTags.find((t) => t.name === 'ไม่คำนวณ')?.id ?? '',
  };

  const questionRows = generateSingleQuestionRows(
    questionPropertiesData,
    difficultyMap,
    calculationMap,
  );

  const parentIdToQuestionRowsMap = groupBy(questionRows, 'parentId');

  const mergedLosRows = qbsTreeWithLos.map((node) => {
    if (node.level !== 'los') return node;

    const matchingQuestionRow = parentIdToQuestionRowsMap[node.id]?.[0];
    if (matchingQuestionRow) {
      return {
        ...node,
        calculate: matchingQuestionRow.calculate,
        nonCalculate: matchingQuestionRow.nonCalculate,
        total: matchingQuestionRow.total,
      };
    }
    return node;
  });

  const qbsTreeWithLosAndQuestionData = mergedLosRows.map((node) => {
    if (node.level === 'los') return node;

    const { calculate, nonCalculate, total } = getCalculateAndNonCalculateTotalFromLOSRow(
      node,
      mergedLosRows,
    );
    return {
      ...node,
      calculate,
      nonCalculate,
      total,
    };
  });

  return qbsTreeWithLosAndQuestionData;
}
